import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { setTickerManualPrice, clearTickerManualPrice } from '../services/api';

const ManualPriceModal = ({ 
  show, 
  onHide, 
  ticker, 
  onPriceSet 
}) => {
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show && ticker) {
      // Initialize with current effective price if available
      const currentPrice = ticker.effective_price || ticker.current_price || ticker.manual_price;
      setPrice(currentPrice ? currentPrice.toString() : '');
      setError('');
      setSuccess('');
    }
  }, [show, ticker]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ticker || !price) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const priceValue = parseFloat(price);
      if (isNaN(priceValue) || priceValue <= 0) {
        throw new Error('Please enter a valid positive price');
      }

      await setTickerManualPrice(ticker.ticker_id, priceValue);
      setSuccess('Manual price set successfully!');
      
      // Call the callback to refresh data
      if (onPriceSet) {
        onPriceSet(ticker.ticker_id, priceValue);
      }

      // Close modal after a short delay
      setTimeout(() => {
        onHide();
      }, 1000);

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to set manual price');
    } finally {
      setLoading(false);
    }
  };

  const handleClearPrice = async () => {
    if (!ticker) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await clearTickerManualPrice(ticker.ticker_id);
      setSuccess('Manual price cleared successfully!');
      
      // Call the callback to refresh data
      if (onPriceSet) {
        onPriceSet(ticker.ticker_id, null);
      }

      // Close modal after a short delay
      setTimeout(() => {
        onHide();
      }, 1000);

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to clear manual price');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPrice('');
    setError('');
    setSuccess('');
    onHide();
  };

  if (!ticker) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Set Manual Price - {ticker.symbol}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Ticker:</strong> {ticker.symbol} - {ticker.name}
        </div>
        
        {ticker.current_price && (
          <div className="mb-3">
            <strong>Current Market Price:</strong> ${ticker.current_price.toFixed(4)}
          </div>
        )}
        
        {ticker.manual_price && (
          <div className="mb-3">
            <strong>Current Manual Price:</strong> ${ticker.manual_price.toFixed(4)}
            {ticker.manual_price_set_at && (
              <div className="text-muted small">
                Set on: {new Date(ticker.manual_price_set_at).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Manual Price ($)</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Enter manual price"
              disabled={loading}
              autoFocus
            />
            <Form.Text className="text-muted">
              This price will override the market price for calculations and display.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        
        {ticker.manual_price && (
          <Button 
            variant="outline-warning" 
            onClick={handleClearPrice}
            disabled={loading}
          >
            {loading ? 'Clearing...' : 'Clear Manual Price'}
          </Button>
        )}
        
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading || !price}
        >
          {loading ? 'Setting...' : 'Set Manual Price'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManualPriceModal;
